/**
 * Diet settings and watch days: validation of what comes back from the database (or from this
 * browser before the diet_settings migration has run), and the browser-storage fallback.
 */
import { parseLayout } from './diaryLayout';
import {
  BMR_MODES, DEFAULT_SETTINGS, ENERGY_MODES,
  type ActivityDay, type ActivityExercise, type ActivityOrigin, type BmrMode, type DietSettings, type EnergyMode, type MacroMode, type MacroPlan, type ManualActivityInput,
} from './types';

const MACRO_MODES: readonly MacroMode[] = ['grams', 'percent', 'protein_fat_first'];
const PROTEIN_BASES: readonly string[] = ['g', 'g_per_kg', 'g_per_lb', 'g_per_kg_lean', 'g_per_lb_lean'];

const num = (v: unknown): number | null => {
  const n: number = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? n : null;
};
const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null);

export function parseMacroPlan(v: unknown): MacroPlan | null {
  const o = obj(v);
  if (!o || !MACRO_MODES.includes(o.mode as MacroMode)) return null;
  const plan: MacroPlan = { mode: o.mode as MacroMode };
  for (const k of ['calories', 'protein_g', 'carbs_g', 'fat_g', 'protein_pct', 'carbs_pct', 'fat_pct', 'protein_amount', 'fat_amount'] as const) {
    const n: number | null = num(o[k]);
    if (n !== null && n >= 0) plan[k] = n;
  }
  if (typeof o.protein_basis === 'string' && PROTEIN_BASES.includes(o.protein_basis)) plan.protein_basis = o.protein_basis as MacroPlan['protein_basis'];
  if (o.fat_basis === 'g' || o.fat_basis === 'pct') plan.fat_basis = o.fat_basis;
  return plan;
}

export function normalizeSettings(raw: unknown): DietSettings {
  const o = obj(raw) ?? {};
  const custom: number | null = num(o.bmr_custom_kcal);
  const bmrCustom: number | null = custom !== null && custom >= 800 && custom <= 5000 ? Math.round(custom) : null;
  let bmrMode: BmrMode = BMR_MODES.includes(o.bmr_mode as BmrMode) ? o.bmr_mode as BmrMode : DEFAULT_SETTINGS.bmr_mode;
  if (bmrMode === 'custom' && bmrCustom === null) bmrMode = 'mifflin';
  const pace: number | null = num(o.goal_pace_pct);
  const tef: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj(o.tef_profile) ?? {})) if (typeof v === 'string' && v.length <= 40) tef[k] = v;
  return {
    energy_mode: ENERGY_MODES.includes(o.energy_mode as EnergyMode) ? o.energy_mode as EnergyMode : DEFAULT_SETTINGS.energy_mode,
    bmr_mode: bmrMode,
    bmr_custom_kcal: bmrCustom,
    include_tef: typeof o.include_tef === 'boolean' ? o.include_tef : DEFAULT_SETTINGS.include_tef,
    macro_plan: parseMacroPlan(o.macro_plan),
    diary_layout: parseLayout(o.diary_layout),
    goal_pace_pct: pace !== null && pace >= -2 && pace <= 2 ? pace : null,
    tef_profile: tef,
  };
}

/** The row sent to diet_settings. */
export function settingsRow(s: DietSettings): Record<string, unknown> {
  return {
    energy_mode: s.energy_mode, bmr_mode: s.bmr_mode, bmr_custom_kcal: s.bmr_custom_kcal, include_tef: s.include_tef,
    macro_plan: s.macro_plan ?? {}, diary_layout: s.diary_layout, goal_pace_pct: s.goal_pace_pct, tef_profile: s.tef_profile,
  };
}

// ---- watch days -------------------------------------------------------------
function parseOrigins(v: unknown): ActivityOrigin[] {
  if (!Array.isArray(v)) return [];
  const out: ActivityOrigin[] = [];
  for (const x of v.slice(0, 20)) {
    if (typeof x === 'string' && x.trim() !== '') { out.push({ package: x, name: null, counted: null }); continue; }
    const o = obj(x);
    if (!o || typeof o.package !== 'string') continue;
    const origin: ActivityOrigin = { package: o.package, name: typeof o.name === 'string' ? o.name : null, counted: typeof o.counted === 'boolean' ? o.counted : null };
    for (const k of ['active_kcal', 'total_kcal', 'steps', 'distance_m', 'exercise_minutes', 'avg_hr', 'min_hr', 'max_hr', 'sleep_minutes'] as const) {
      const n: number | null = num(o[k]);
      if (n !== null && n >= 0) origin[k] = n;
    }
    out.push(origin);
  }
  return out;
}

function parseExercises(v: unknown): ActivityExercise[] {
  if (!Array.isArray(v)) return [];
  const out: ActivityExercise[] = [];
  for (const x of v.slice(0, 50)) {
    const o = obj(x);
    if (!o) continue;
    const minutes: number | null = num(o.minutes);
    out.push({
      title: typeof o.title === 'string' && o.title.trim() !== '' ? o.title.trim() : null,
      type: typeof o.type === 'string' ? o.type : 'other',
      start: typeof o.start === 'string' ? o.start : '',
      end: typeof o.end === 'string' ? o.end : '',
      minutes: minutes !== null && minutes >= 0 ? minutes : 0,
      ...(num(o.kcal) !== null ? { kcal: num(o.kcal) as number } : {}),
    });
  }
  return out;
}

export function toActivityDay(r: Record<string, unknown>): ActivityDay {
  const n = (k: string): number | null => num(r[k]);
  return {
    local_date: String(r.local_date),
    source: r.source === 'manual' ? 'manual' : 'health_connect',
    active_kcal: n('active_kcal'), total_kcal: n('total_kcal'), steps: n('steps'), steps_uncovered: n('steps_uncovered'),
    distance_m: n('distance_m'), exercise_minutes: n('exercise_minutes'), avg_hr: n('avg_hr'), min_hr: n('min_hr'), max_hr: n('max_hr'),
    resting_hr: n('resting_hr'), hrv_rmssd_ms: n('hrv_rmssd_ms'), spo2_pct: n('spo2_pct'), respiratory_rate: n('respiratory_rate'),
    vo2max: n('vo2max'), sleep_minutes: n('sleep_minutes'),
    exercises: parseExercises(r.exercises), origins: parseOrigins(r.origins),
    day_complete: r.day_complete === true, notes: typeof r.notes === 'string' ? r.notes : null,
    synced_at: typeof r.synced_at === 'string' ? r.synced_at : '',
  };
}

/** A typed-in day as a full row (for the browser fallback and the demo). */
export function manualDay(input: ManualActivityInput, today: string): ActivityDay {
  return {
    local_date: input.local_date, source: 'manual',
    active_kcal: input.active_kcal, total_kcal: input.total_kcal, steps: input.steps, steps_uncovered: null, distance_m: null, exercise_minutes: null,
    avg_hr: null, min_hr: null, max_hr: null, resting_hr: input.resting_hr, hrv_rmssd_ms: input.hrv_rmssd_ms,
    spo2_pct: null, respiratory_rate: null, vo2max: null, sleep_minutes: input.sleep_minutes,
    exercises: [], origins: [], day_complete: input.local_date < today, notes: input.notes, synced_at: new Date().toISOString(),
  };
}

/** Checks a typed-in day against the database limits. Returns the first problem, or null. */
export function manualActivityProblem(i: ManualActivityInput): string | null {
  const within = (v: number | null, lo: number, hi: number): boolean => v === null || (v >= lo && v <= hi);
  if (i.active_kcal === null && i.total_kcal === null && i.steps === null && i.resting_hr === null && i.hrv_rmssd_ms === null && i.sleep_minutes === null) {
    return 'Enter at least one number from your watch.';
  }
  if (!within(i.active_kcal, 0, 15000)) return 'Active calories must be between 0 and 15,000.';
  if (!within(i.total_kcal, 0, 20000)) return 'Total calories must be between 0 and 20,000.';
  if (!within(i.steps, 0, 300000)) return 'Steps must be between 0 and 300,000.';
  if (!within(i.resting_hr, 20, 200)) return 'Resting heart rate must be between 20 and 200.';
  if (!within(i.hrv_rmssd_ms, 1, 500)) return 'HRV must be between 1 and 500 ms.';
  if (!within(i.sleep_minutes, 0, 1440)) return 'Sleep must be between 0 and 24 hours.';
  return null;
}

// ---- browser fallback (before the migration runs) ---------------------------
const SETTINGS_KEY = (uid: string): string => `mightypeanut.settings.${uid}`;
const ACTIVITY_KEY = (uid: string): string => `mightypeanut.activity.${uid}`;

export function readLocalSettings(uid: string): DietSettings | null {
  try {
    const raw: string | null = localStorage.getItem(SETTINGS_KEY(uid));
    return raw ? normalizeSettings(JSON.parse(raw)) : null;
  } catch { return null; }
}

export function writeLocalSettings(uid: string, s: DietSettings): void {
  try { localStorage.setItem(SETTINGS_KEY(uid), JSON.stringify(settingsRow(s))); } catch { /* storage unavailable */ }
}

export function clearLocalSettings(uid: string): void {
  try { localStorage.removeItem(SETTINGS_KEY(uid)); } catch { /* storage unavailable */ }
}

export function readLocalActivity(uid: string): ActivityDay[] {
  try {
    const raw: string | null = localStorage.getItem(ACTIVITY_KEY(uid));
    const rows: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(rows) ? rows.map((r) => toActivityDay(r as Record<string, unknown>)) : [];
  } catch { return []; }
}

export function writeLocalActivity(uid: string, rows: ActivityDay[]): void {
  try { localStorage.setItem(ACTIVITY_KEY(uid), JSON.stringify(rows)); } catch { /* storage unavailable */ }
}
